import React, { useLayoutEffect, useMemo } from 'react';

import { BrowserRouter, Route, Routes } from 'react-router-dom';


import { Provider } from 'react-redux';
import { useAppSelector, useAppDispatch } from '@/ui/store/helperRedux';
import { RootState, store } from '@/ui/store/store';
import { updateStoreState } from '@/ui/store/slices/storeDataSlice';
import { setLanguageRedux } from '@/ui/store/slices/languageSlice';

import StoreController from '@/controllers/StoreController';

import { IStore } from '@/domain/interfaces/IStore';

import '@/ui/styles/index.scss';

import StoreRouteHandler from '@/ui/components/store/StoreRouteHandler';

import Layout from '@/ui/containers/Layout';

import NotFound from '@/ui/pages/NotFound';
import Library from '@/ui/pages/Library';

import UserInfo from '@/ui/pages/user/UserInfo';
import UserPassword from '@/ui/pages/user/UserPassword';

import Store from '@/ui/pages/Store';
import ProductDetails from '@/ui/pages/product/ProductDetails';

import AddProducts from '@/ui/pages/inventory/AddProducts';
import ProductsValidationInterface from '@/ui/pages/inventory/ProductsValidationInterface';
import Inventory from '@/ui/pages/inventory';
import MassiveCharge from '@/ui/pages/inventory/MassiveCharge';

import Cart from '@/ui/pages/sales/Cart';
import CartCheckOut from '@/ui/pages/sales/CartCheckOut/CartCheckOut';
import SalesHistory from '@/ui/pages/sales/SalesHistory';
import TransbankResponse from '@/ui/pages/sales/TransbankResponse';
import ErrorPage from '@/ui/pages/ErrorPage';
import Home from '@/ui/pages/Home';
import SearchOrder from '@/ui/pages/sales/SearchOrder';
import ConstructionPage from '@/ui/pages/ConstructionPage';
import TrackingOrderDetails from '@/ui/pages/sales/TrackingOrderDetails';
import UserPasswordCode from '@/ui/pages/user/UserPasswordCode';
import UserResetPassword from '@/ui/pages/user/UserResetPassword';
import Purchases from '@/ui/pages/user/Purchases';
import Blog from '@/ui/pages/Store/Blog';
import { getRoutesDirections } from '@/data/libraries/RoutesTranslated';
import { updateI18nLanguage } from '@/domain/utils/i18n';


/** Componente de configuración de rutas y estados de la aplicación
 * @component
 */

const AppContent = (): JSX.Element => {

  /** Controlador tienda */
  const { getStoreData, getLanguaje, setLanguage } = StoreController();

  const dispatch = useAppDispatch();

  /** Seleccionamos el idioma desde Redux */
  //const language = useAppSelector((state: RootState) => state.language.language);

  /** Obtenemos el idioma y diccionario almacenado en el localStorage */
  const cookieLang: string | null = getLanguaje();

  /** Recalculamos las rutas cuando cambia el idioma */
  //const routesDirections = useMemo(() => getRoutesDirections(language), [language]);
  const routesDirections = getRoutesDirections(cookieLang ?? 'es');

  /** Verificamos si existe el idioma almacenado en el localStorage */
  if (cookieLang) {
    /** Actualizamos el idioma en Redux */
    dispatch(setLanguageRedux(cookieLang));
    updateI18nLanguage(cookieLang);
  }

  /** Configura data de la tienda y lenguaje  */
  const configureStoreData = async (): Promise<void> => {
    try {
      /** Obtenemos la data de la tienda */
      const storeData: IStore = await getStoreData();
      if (storeData) {
        /** Guardamos la data de la tienda en el localStorage */
        dispatch(updateStoreState({ ...storeData }));
        /** Guardamos el idioma en el localStorage si a cambiado el lenguaje */
        if (storeData.language !== cookieLang) {
          dispatch(setLanguageRedux(storeData.language));
          updateI18nLanguage(storeData.language);
          setLanguage(storeData.language);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useLayoutEffect(() => {
    document.title = process.env.REACT_APP_TITLE ?? '';
    configureStoreData();
  }, []);

  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true }}>
      <Layout>
        <Routes>
          <Route path={routesDirections.MAIN_ROUTE} element={<Home />} />
          <Route path={routesDirections.ERROR_ROUTE} element={<ErrorPage />} />
          <Route path={routesDirections.NOT_FOUND_ROUTE} element={<NotFound />} />
          <Route path={routesDirections.USER_INFO_ROUTE} element={<UserInfo />} />
          <Route path={routesDirections.CHANGE_PASSWORD_ROUTE} element={<UserPassword />} />
          <Route path={routesDirections.PASSWORD_CODE_ROUTE} element={<UserPasswordCode />} />
          <Route path={routesDirections.PASSWORD_RESET_ROUTE} element={<UserResetPassword />} />
          <Route path={routesDirections.STORE_ROUTE} element={<StoreRouteHandler />} />
          <Route path={routesDirections.STORE_BY_CATEGORY_ROUTE} element={<Store />} />
          <Route path={routesDirections.STORE_BY_PAGE} element={<Store />} />
          <Route path={routesDirections.PRODUCT_DETAILS_ROUTE} element={<ProductDetails />} />
          <Route path={routesDirections.CART_ROUTE} element={<Cart />} />
          <Route path={routesDirections.CHECK_OUT_ROUTE} element={<CartCheckOut />} />
          <Route path={routesDirections.SALES_HISTORY_ROUTE} element={<SalesHistory />} />
          <Route path={routesDirections.TRANSBANK_RESPONSE_ROUTE} element={<TransbankResponse />} />
          <Route path={routesDirections.INVENTORY_ROUTE} element={<Inventory />} />
          <Route path={routesDirections.MASSIVE_CHARGE_ROUTE} element={<MassiveCharge />} />
          <Route path={routesDirections.INSERT_VALIDATION_INTERFACE_ROUTE} element={<ProductsValidationInterface />} />
          <Route path={routesDirections.ADD_PRODUCTS_FORM_WIZARD_ROUTE} element={<AddProducts />} />
          <Route path={routesDirections.PURCHASES_ROUTE} element={<Purchases />} />
          <Route path={routesDirections.BLOG_ROUTE} element={<Blog />} />
          <Route path={routesDirections.TRACKING_ROUTE} element={<SearchOrder />} />
          <Route path={routesDirections.TRACKING_ROUTE_DETAIL} element={<TrackingOrderDetails />} />
          <Route path={routesDirections.CONSTRUCTION_ROUTE} element={<ConstructionPage />} />
          <Route path={routesDirections.LIBRARY_ROUTE} element={<Library />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
};

/** Componente de la aplicación */
const App = (): JSX.Element => {
  return (
    <React.StrictMode>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </React.StrictMode>
  );
};

export default App;
